import { useState, useEffect } from 'react';
import { useSwrTick } from '@/lib/swrRefresh';
import axios from 'axios';
import { Wallet, Coins, Calculator, Receipt, BarChart3, Download, Upload } from 'lucide-react';
import InfoBox from '@/components/InfoBox';
import CalculatorModal from '@/components/CalculatorModal';
import CashCard from '@/components/CashCard';
import DividendsCollectedModal from '@/components/DividendsCollectedModal';
import { usePortfolioScopeParams } from '@/lib/portfolioScopeParams';
import { advancePortfolioLoadStage, usePortfolioLoadStage } from '@/lib/portfolioLoadOrder';  // eslint-disable-line no-unused-vars
import { dedupedGet } from '@/lib/dedupedGet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CapitalTrackerCard — lifetime cash-flow ledger for the portfolio.
 *
 * Surfaces four numbers pulled from `/portfolio/summary`:
 *
 *   • Total Contributions    — fresh outside money put in
 *                              (deposits + outside-portion of every buy)
 *   • Total Withdrawn        — cash pulled back out (manual withdrawals)
 *   • Net Contributions      — out-of-pocket cost: contributions − withdrawn
 *   • Dividends Collected    — lifetime cash dividends paid in
 *
 * Plus a derived "Net Gain on Capital" pill = current Total Value −
 * Net Contributions − Dividends Collected. That's the pure unrealized
 * appreciation on the user's outside money.
 *
 * Rationale: at age 72 the user plans to flip DRIP off and live on
 * $1k/mo cash withdrawals. They need an honest, glanceable ledger of
 * "how much fresh money have I put in" vs "how much have I taken out
 * over my lifetime", separate from the daily moving Total Value
 * number that responds to share prices.
 *
 * Re-fetches on every `cash-changed` and `portfolio-changed` global
 * event so add/withdraw/buy/sell flows all stay in sync without prop
 * drilling.
 */
export default function CapitalTrackerCard({ refreshKey, onTaxLotsClick, onYearEndClick, onTaxAuditClick, onExportCsv, onImportCsv }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bump, setBump] = useState(0);
  // Calculator modal — launched from the right side of this card's title.
  const [calcOpen, setCalcOpen] = useState(false);
  const [divsOpen, setDivsOpen] = useState(false);
  // Round 92o++++++++N++++++++++ (Jun 2026) — Plus multi-portfolio:
  // scope the lifetime ledger to the selected portfolio so Total
  // Contributions / Net / Dividends Collected stop doubling when the
  // user owns multiple portfolios.
  const scopeParams = usePortfolioScopeParams();
  const scopeKey = scopeParams.portfolio_id || 'all';
  // Round P124 (Feb 2026) — Gate on the shared load-order counter
  // so the Capital Tracker fetch waits until the My Portfolio
  // summary has landed. Prevents this fetch from queueing on the
  // browser's 6-concurrent HTTP limit alongside 20+ other
  // portfolio-page fetches, which was starving the summary
  // endpoint even though it only takes ~200 ms server-side.
  const loadStage = usePortfolioLoadStage();

  const swrTick = useSwrTick('/portfolio/summary');
  useEffect(() => {
    if (loadStage < 2) return undefined;
    let cancelled = false;
    setLoading(true);
    // Sep 2026 — always-live headline (same payload keys, no snapshot); fires
    // the moment the My Portfolio card has painted (stage 2).
    dedupedGet(`${API}/portfolio/headline`, { params: scopeParams, withCredentials: true })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey, bump, scopeKey, loadStage, swrTick]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const h = () => setBump((b) => b + 1);
    window.addEventListener('cash-changed', h);
    window.addEventListener('portfolio-changed', h);
    return () => {
      window.removeEventListener('cash-changed', h);
      window.removeEventListener('portfolio-changed', h);
    };
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-4 mb-3 flex items-center justify-center h-20" data-testid="capital-tracker-loading">
        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totalContributions = data ? (Number(data.total_contributions) || 0) : 0;
  const totalWithdrawn = data ? (Number(data.total_withdrawn) || 0) : 0;
  const totalWithdrawnInside = data ? (Number(data.total_withdrawn_inside) || 0) : 0;
  const netContributions = data ? (Number(data.net_contributions) || 0) : 0;
  const dividendsCollected = data ? (Number(data.total_dividends_collected) || 0) : 0;

  // Round 91 — Capital Tracker now ALWAYS renders because the Cash
  // Balance ribbon lives inside it (merged from a separate card per
  // user request). The metric tiles (Contributions/Withdrawn/Net/
  // Dividends) self-hide for a fresh empty portfolio so the box
  // doesn't shout zeros at a new user, but the Cash Balance row + its
  // action buttons stay so the user can fund the account immediately.
  const hasLedgerData = totalContributions !== 0 || totalWithdrawn !== 0 || dividendsCollected !== 0;

  const fmt = (n) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="rounded-lg border bg-card p-4 mb-3" data-testid="capital-tracker-card">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-950/40 flex items-center justify-center">
          <Wallet className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        </div>
        <h2 className="text-base font-bold">Capital Tracker</h2>
        <InfoBox className="text-muted-foreground" tooltip={
          <>
            <p className="font-bold mb-1">Lifetime cash-flow ledger.</p>
            <p className="mb-1.5"><b>Total Contributions:</b> fresh outside money put in (deposits + outside-portion of every buy).</p>
            <p className="mb-1.5"><b>Total Withdrawn:</b> <i>outside</i> cash you've pulled back out (principal coming home). Inside withdrawals — dividends or sale proceeds drawn down — are tracked separately and do NOT touch this number.</p>
            <p className="mb-1.5"><b>Net Contributions:</b> your true out-of-pocket cost (Contributions − outside Withdrawn). The "honest" measure for tax + savings tracking.</p>
            <p><b>Dividends Collected:</b> lifetime cash dividends. Portfolio-generated income, not contributions.</p>
          </>
        } />
        {/* Calculator launcher — sits opposite the title on the same
            line. Darker fuchsia (rebalancer-family) in light theme,
            beige in dark. Compact "Calc" label on phone, "Calculator"
            from sm: up so it fits the row alongside the title block. */}
        <button
          type="button"
          onClick={() => setCalcOpen(true)}
          data-testid="capital-tracker-calculator-btn"
          className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[13px] font-semibold border border-fuchsia-900/40 dark:border-amber-200/40 text-fuchsia-900 dark:text-amber-100 bg-fuchsia-50/60 hover:bg-fuchsia-100 dark:bg-amber-950/30 dark:hover:bg-amber-900/40 transition-colors"
          title="Open calculator"
        >
          <Calculator className="w-[17px] h-[17px]" />
          <span className="sm:hidden">Calc</span>
          <span className="hidden sm:inline">Calculator</span>
        </button>
      </div>

      {/* Round 91 — Cash Balance ribbon embedded in Capital Tracker.
          Sits below the title bar so the user sees their current cash
          first, then the lifetime ledger tiles. */}
      <div className="mb-3 pb-3 border-b">
        <CashCard embedded />
      </div>

      {hasLedgerData ? (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-stretch">
        <div className="text-center flex flex-col" title="Lifetime fresh outside money put into the portfolio. Deposits + outside-portion of every buy.">
          <p className="text-[12px] text-muted-foreground mb-1">Total Contributions</p>
          <p className="text-lg font-bold font-mono text-foreground" data-testid="capital-contributions">{fmt(totalContributions)}</p>
          <p className="text-[11px] text-muted-foreground">money in</p>
          {onTaxLotsClick && (
            <button
              onClick={onTaxLotsClick}
              data-testid="capital-tracker-tax-lots-btn"
              title="Lot-by-lot unrealized G/L with long-term vs short-term tagging and tax-loss harvest candidates"
              className="mt-auto pt-2 inline-flex items-center justify-center gap-1.5 self-center px-2.5 py-1.5 rounded-md text-[15px] font-semibold border border-border bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
            >
              <Receipt className="w-[19px] h-[19px] text-cyan-500 dark:text-cyan-400" />
              <span>Tax Lots</span>
            </button>
          )}
        </div>
        <div className="text-center flex flex-col" title="Lifetime OUTSIDE cash pulled back out (principal coming home). Inside withdrawals — dividends or sale proceeds drawn down — are tracked separately.">
          <p className="text-[12px] text-muted-foreground mb-1">Total Withdrawn</p>
          <p className="text-lg font-bold font-mono text-orange-600 dark:text-orange-400" data-testid="capital-withdrawn">{fmt(totalWithdrawn)}</p>
          <p className="text-[11px] text-muted-foreground">
            money out
            {totalWithdrawnInside > 0 && (
              <span className="block text-[10px] opacity-80" title={`Plus ${fmt(totalWithdrawnInside)} of inside cash (dividends / sale proceeds) drawn down — does NOT reduce Net Contributions.`}>
                +{fmt(totalWithdrawnInside)} inside
              </span>
            )}
          </p>
          {(onYearEndClick || onTaxAuditClick) && (
            <div className="mt-auto pt-2 flex flex-col items-center gap-1.5" data-testid="capital-tracker-tax-buttons">
              {onTaxAuditClick && (
                <button
                  onClick={onTaxAuditClick}
                  data-testid="capital-tracker-tax-audit-btn"
                  title="Tax Account Audit — performance & dividends broken out per Taxable / Roth / Trad IRA / 401(k) / HSA / Other."
                  className="inline-flex items-center justify-center gap-1.5 self-center px-2.5 py-1.5 rounded-md text-[15px] font-semibold border border-border bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
                >
                  <Wallet className="w-[19px] h-[19px] text-violet-500 dark:text-violet-400" />
                  <span>Account Audit</span>
                </button>
              )}
              {onYearEndClick && (
                <button
                  onClick={onYearEndClick}
                  data-testid="capital-tracker-year-end-btn"
                  title="One-page year-end portfolio snapshot — cost basis, current value, dividends, realised G/L. Print or CSV for tax season."
                  className="inline-flex items-center justify-center gap-1.5 self-center px-2.5 py-1.5 rounded-md text-[15px] font-semibold border border-border bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
                >
                  <BarChart3 className="w-[19px] h-[19px] text-amber-500 dark:text-amber-400" />
                  <span>Year-End</span>
                </button>
              )}
            </div>
          )}
        </div>
        <div className="text-center flex flex-col" title="Net out-of-pocket cost: Contributions − Withdrawn. The 'honest' measure of what you've actually invested over the portfolio's lifetime. Sales don't count as withdrawals — proceeds stay inside as Cash Balance.">
          <p className="text-[12px] text-muted-foreground mb-1">Net Contributions</p>
          <p className="text-lg font-bold font-mono text-blue-700 dark:text-blue-400" data-testid="capital-net">{fmt(netContributions)}</p>
          <p className="text-[11px] text-muted-foreground">true out-of-pocket · sales stay inside</p>
          {onExportCsv && (
            <button
              onClick={onExportCsv}
              data-testid="capital-tracker-export-csv-btn"
              title="Export your full portfolio as a CSV file"
              className="mt-auto pt-2 inline-flex items-center justify-center gap-1.5 self-center px-2.5 py-1.5 rounded-md text-[15px] font-semibold border border-border bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
            >
              <Download className="w-[19px] h-[19px] text-amber-500 dark:text-amber-400" />
              <span>Export CSV</span>
            </button>
          )}
        </div>
        <div className="text-center flex flex-col">
          <button
            type="button"
            onClick={() => setDivsOpen(true)}
            title="Lifetime dividends received (cash + DRIP). Portfolio-generated income, NOT a contribution. Click or tap for the full list."
            className="self-center text-center rounded-md focus-visible:outline-none focus-visible:ring-2 transition-colors px-3 py-1 cursor-pointer border-2 border-green-400 dark:border-green-700 hover:bg-green-50/60 dark:hover:bg-green-950/30 focus-visible:ring-green-500/40"
            data-testid="capital-dividends-button"
          >
            <p className="text-[12px] text-muted-foreground mb-1 inline-flex items-center gap-1 justify-center">
              <Coins className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
              Dividends Collected
            </p>
            <p className="text-lg font-bold font-mono text-emerald-700 dark:text-emerald-400" data-testid="capital-dividends">{fmt(dividendsCollected)}</p>
            <p className="text-[11px] text-muted-foreground">portfolio income</p>
            <p className="text-[10px] text-violet-500 dark:text-violet-400 mt-0.5" data-testid="capital-dividends-hint">Click or Tap for List</p>
          </button>
          <DividendsCollectedModal open={divsOpen} onClose={() => setDivsOpen(false)} portfolioId={scopeParams.portfolio_id} total={dividendsCollected} />
          {onImportCsv && (
            <button
              onClick={onImportCsv}
              data-testid="capital-tracker-import-csv-btn"
              title="Import positions from a CSV exported by your broker or DiviDrip"
              className="mt-auto pt-2 inline-flex items-center justify-center gap-1.5 self-center px-2.5 py-1.5 rounded-md text-[15px] font-semibold border border-border bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
            >
              <Upload className="w-[19px] h-[19px] text-emerald-500 dark:text-emerald-400" />
              <span>Import CSV</span>
            </button>
          )}
        </div>
      </div>
      ) : (
        // Round 92o++++++++N++++++++++ (Jun 2026) — Empty-state action
        // bar. When a freshly-created Plus portfolio has zero ledger
        // data, the ledger tiles + their embedded action buttons used
        // to disappear entirely, forcing the user back to the Tool Box
        // to find Import CSV / Tax Audit / etc. Now the buttons render
        // in a compact wrap-friendly row so the user can fund their
        // brand-new portfolio in-place.
        <div className="flex flex-wrap gap-2 items-center justify-center" data-testid="capital-tracker-empty-actions">
          {onImportCsv && (
            <button
              onClick={onImportCsv}
              data-testid="capital-tracker-import-csv-btn"
              title="Import positions from a CSV exported by your broker or DiviDrip"
              className="inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md text-[14px] font-semibold border border-border bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
            >
              <Upload className="w-[18px] h-[18px] text-emerald-500 dark:text-emerald-400" />
              <span>Import CSV</span>
            </button>
          )}
          {onExportCsv && (
            <button
              onClick={onExportCsv}
              data-testid="capital-tracker-export-csv-btn"
              title="Export your full portfolio as a CSV file"
              className="inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md text-[14px] font-semibold border border-border bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
            >
              <Download className="w-[18px] h-[18px] text-amber-500 dark:text-amber-400" />
              <span>Export CSV</span>
            </button>
          )}
          {onTaxLotsClick && (
            <button
              onClick={onTaxLotsClick}
              data-testid="capital-tracker-tax-lots-btn"
              title="Lot-by-lot unrealized G/L with long-term vs short-term tagging"
              className="inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md text-[14px] font-semibold border border-border bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
            >
              <Receipt className="w-[18px] h-[18px] text-cyan-500 dark:text-cyan-400" />
              <span>Tax Lots</span>
            </button>
          )}
          {onTaxAuditClick && (
            <button
              onClick={onTaxAuditClick}
              data-testid="capital-tracker-tax-audit-btn"
              title="Tax Account Audit — performance & dividends broken out per account type"
              className="inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md text-[14px] font-semibold border border-border bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
            >
              <Wallet className="w-[18px] h-[18px] text-violet-500 dark:text-violet-400" />
              <span>Account Audit</span>
            </button>
          )}
          {onYearEndClick && (
            <button
              onClick={onYearEndClick}
              data-testid="capital-tracker-year-end-btn"
              title="One-page year-end portfolio snapshot"
              className="inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md text-[14px] font-semibold border border-border bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
            >
              <BarChart3 className="w-[18px] h-[18px] text-amber-500 dark:text-amber-400" />
              <span>Year-End</span>
            </button>
          )}
        </div>
      )}
      <CalculatorModal open={calcOpen} onClose={() => setCalcOpen(false)} />
    </div>
  );
}
